import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import Modal, { ModalButton } from "common/modal";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Spin, Alert, message, Input } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
// GraphQL
import GET_SURVEY_QUESTION from "graphql/queries/GetSurveyQuestion";
import CREATE_SURVEY_RESPONSE from "graphql/mutations/CreateSurveyResponse";
import CREATE_SURVEY from "graphql/mutations/CreateSurvey";
// Styles
import {
  AnswersCard,
  CardContent,
  StyledCheckbox,
  CardGrid,
  ModalInput,
} from "./CancelSubscriptionModalStyle";

interface CancelSubscriptionModalProps {
  loading: boolean;
  visible: boolean;
  isTrialPackageModal?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onChange?: (data: { reasons: string[] }) => void;
  studentId?: number;
}

const ModalText = styled.div`
  margin: 8px 0px 12px;
  font-size: 18px;
  color: #282828;
`;

const CancelSubscriptionModal = ({
  loading,
  visible,
  isTrialPackageModal,
  onConfirm,
  onCancel,
  onChange = () => {
    // no-op
  },
  studentId,
}: CancelSubscriptionModalProps) => {
  const { t } = useTranslation("my_credits");
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [otherReason, setOtherReason] = useState("");

  // GraphQL
  const { error, data } = useQuery(GET_SURVEY_QUESTION, {
    variables: {
      order: 0,
      surveyType: 1,
    },
  });
  const [survey] = useMutation(CREATE_SURVEY);
  const [createSurveyResponse] = useMutation(CREATE_SURVEY_RESPONSE, {
    onError: (errorCreateResponse) => {
      message.error(`Error saving response: ${errorCreateResponse.message}`);
    },
  });

  if (loading) return <Spin />;
  if (error) return <Alert message="Error loading question" type="error" />;

  const surveyQuestion = data?.surveyQuestion;
  if (!surveyQuestion)
    return <Alert message="Question not found" type="warning" />;

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    setSelectedReasons((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const handleCreateSurveyResponse = async () => {
    try {
      if (otherReason) {
        await createSurveyResponse({
          variables: {
            input: {
              attributes: {
                surveyQuestionId: surveyQuestion.id,
                surveyAnswerId: null,
                studentId,
                customAnswer: otherReason,
              },
            },
          },
        });
      }
      if (selectedReasons.length > 0) {
        onChange({ reasons: selectedReasons });

        await Promise.all(
          selectedReasons.map(async (reasonId) => {
            await createSurveyResponse({
              variables: {
                input: {
                  attributes: {
                    surveyQuestionId: surveyQuestion.id,
                    surveyAnswerId: reasonId,
                    studentId,
                  },
                },
              },
            });
          })
        );
      }
    } catch (e) {
      console.error("Error saving survey response:", e);
    }
  };

  const handleCreateSurvey = async () => {
    const surveyType = "SUBSCRIPTION";
    if (surveyType) {
      try {
        await survey({
          variables: {
            input: {
              surveyType,
              studentId,
            },
          },
        });
      } catch (e) {
        console.error("Error saving CANCEL_SUBSCRIPTION_SURVEY:", e);
      }
    }
  };

  const handleOtherReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherReason(e.target.value);
  };

  return (
    <Modal
      title={
        isTrialPackageModal ? t("CANCEL PACKAGE") : t("CANCEL SUBSCRIPTION")
      }
      visible={visible}
      onCancel={onCancel}
      className="cancel-subscription-modal specific-modal"
    >
      <ModalText>
        {t(
          "We're sorry to see you go! We'd love to understand more about your needs so we can make our product better. Could you please share your reason with us?"
        )}
        <br />
        <AnswersCard>
          {surveyQuestion.surveyAnswers.map((answer: any) => (
            <CardGrid key={answer.id}>
              <CardContent
                isSelected={selectedReasons.includes(answer.id)}
                onClick={() =>
                  handleCheckboxChange({
                    target: {
                      value: answer.id,
                      checked: !selectedReasons.includes(answer.id),
                    },
                  } as CheckboxChangeEvent)
                }
              >
                <span>{answer.answerText}</span>
                <StyledCheckbox
                  checked={selectedReasons.includes(answer.id)}
                  value={answer.id}
                />
              </CardContent>
            </CardGrid>
          ))}
        </AnswersCard>
        <ModalInput>
          <br />
          {t("Is there anything we can do for you?")}
          <br />
          <Input
            className="cancel-subscription other-reason-input"
            placeholder="Other reason..."
            value={otherReason}
            onChange={handleOtherReasonChange}
          />
        </ModalInput>
        <br />
        {t("Is there anything we can do for you?")}
        {t("Just email us at")}{" "}
        <a href="mailto:support@flexiclasses.com?subject=Cancelling&body=">
          support@flexiclasses.com
        </a>
      </ModalText>

      <br />

      <ModalButton
        loading={loading}
        onClick={() => {
          onConfirm();
          handleCreateSurveyResponse();
          handleCreateSurvey();
        }}
        disabled={selectedReasons.length === 0 && !otherReason}
      >
        {t("CANCEL SUBSCRIPTION")}
      </ModalButton>
    </Modal>
  );
};

export default CancelSubscriptionModal;
